import {
  CALENDAR_ENTRY_GROUP,
  CALENDAR_ROW,
  CALENDAR_SPOT_INFO,
  CAPTION_IDEA,
  ENTRY_BY_TYPE,
  Id,
  INQUIRY_FILTER,
  MARKETING_FILTER,
} from '@/calendar-types'
import { calendarFilters } from '@/constants'
import { findDifferences } from '@/utils/calendar-utils'
import { calendarEndpoints } from '@/utils/endpoints'
import {
  mediaEditorInfoEditedShownEvent,
  mediaEditorInfoFailedShownEvent,
} from '@/utils/mixpanel-calendar'
import { sleep } from '@/utils/parsers'
import type { DateValue } from '@zag-js/date-picker'
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  set,
  startOfMonth,
} from 'date-fns'
import { defu } from 'defu'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useCalendarStore = defineStore('calendar', () => {
  const appStore = useAppStore()
  const thumbnailStore = useThumbnailStore()
  const route = useRoute()
  const entries = ref<ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE][][]>([])

  const websitePromotions = ref<ENTRY_BY_TYPE['website'][]>([])
  const blockedDays = ref<ENTRY_BY_TYPE['blocker'][]>([])
  const authStore = useAuthStore()
  const spotInfo = ref<CALENDAR_SPOT_INFO>()
  const userChangedText = ref(false)

  const inquiryFiltersStorage = useStorage<
    | {
        inquiry: INQUIRY_FILTER[]
        marketing: MARKETING_FILTER[]
        waitingOnMeFilter: boolean
      }
    | Record<string, never>
  >(`calendar_filters_${authStore.spotId}`, {})

  const {
    isActive: isEntryProcessPollingActive,
    pause: pauseStudioStatusUpdatePolling,
    resume: resumeStudioStatusUpdatePolling,
  } = useTimeoutPoll(studioStatusUpdate, 5000, { immediate: false })

  const inquiryFilters = ref<INQUIRY_FILTER[]>([])
  const marketingFilters = ref<MARKETING_FILTER[]>([])
  const waitingOnMeFilter = ref(false)

  const showDatePickerPopUp = ref(false)
  const showEntryDialog = ref(false)

  const selectedEntry =
    ref<
      ENTRY_BY_TYPE[Exclude<
        keyof ENTRY_BY_TYPE,
        'blocker' | 'content_nudge' | 'holiday_nudge'
      >]
    >()

  const showGroupDialog = ref(false)
  const selectedGroup = ref<CALENDAR_ENTRY_GROUP>()
  
const selectedDate = computed(() => {
  return appStore.appReady && appStore.date 
    ? appStore.date.now() 
    : new Date()
})

  const showPostApprovedBtn = ref(false)
  const showSaveChangesDialog = ref(false)
  const showDeleteConfirmationDialog = ref(false)

  const selectedCaptionIdea = ref<CAPTION_IDEA>()
  const captionIdeas = ref<CAPTION_IDEA[]>([])

  const currentMonth = ref(
    set(new Date(), {
      year: new Date().getFullYear(),
      month: new Date().getMonth(),
      date: new Date().getDate(),
      hours: new Date().getHours(),
      minutes: 0,
      seconds: 0,
    }),
  )

  async function setCurrentMonth(value: DateValue) {
    currentMonth.value = set(new Date(), {
      year: value.year,
      month: value.month - 1,
      date: value.day,
    })

    await fetchCalendarEntries()

    if (
      marketingFilters.value.filter((filter) => filter.type === 'website')[0]
        ?.active
    ) {
      await fetchCalendarPromotions()
    }
  }

  const daysInMonth = computed(() => {
    return eachDayOfInterval({
      start: startOfMonth(currentMonth.value),
      end: endOfMonth(currentMonth.value),
    })
  })

  const calendarWithEntries = computed(() => {
    return daysInMonth.value.map((day, index): CALENDAR_ROW => {
      return {
        entries: Array.isArray(entries?.value[index])
          ? entries.value[index]
          : undefined,

        weekDay: day,
      }
    })
  })

  async function fetchCalendarEntries() {
    appStore.isLoading = true
    const searchParams = new URLSearchParams()
    searchParams.set('start_date', format(daysInMonth.value[0], 'yyyy-MM-dd'))
    searchParams.set(
      'end_date',
      format(daysInMonth.value[daysInMonth.value.length - 1], 'yyyy-MM-dd'),
    )

    const { data } = await useApi(
      `${calendarEndpoints().getCalendarEntries(authStore.spotId)}?${searchParams.toString()}`,
    )
      .post({
        spotId: authStore.spotId,
        waiting_on_me: waitingOnMeFilter.value,
        content_nudge: appStore.spotHopperEmbedded,
        holiday_nudge: appStore.spotHopperEmbedded,
        filters: {
          inquiry: inquiryFilters.value,
          marketing: marketingFilters.value,
        },
      })
      .json()

    entries.value = data.value

    await sleep(500)

    appStore.isLoading = false
  }

  async function fetchCalendarPromotions() {
    const searchParams = new URLSearchParams()
    searchParams.set('start_date', format(daysInMonth.value[0], 'yyyy-MM-dd'))
    searchParams.set(
      'end_date',
      format(daysInMonth.value[daysInMonth.value.length - 1], 'yyyy-MM-dd'),
    )

    const { data } = await useApi(
      `${calendarEndpoints().getCalendarPromotions(authStore.spotId)}?${searchParams.toString()}`,
    )
      .get()
      .json()

    websitePromotions.value = data.value
  }

  async function fetchCalendarBlockedDays() {
    const searchParams = new URLSearchParams()
    searchParams.set('start_date', format(daysInMonth.value[0], 'yyyy-MM-dd'))
    searchParams.set('end_date', format(endOfYear(new Date()), 'yyyy-MM-dd'))

    const { data } = await useApi(
      `${calendarEndpoints().getCalendarBlockedDays(authStore.spotId)}?${searchParams.toString()}`,
    )
      .get()
      .json()

    blockedDays.value = data.value
  }

  async function fetchCalendarSpotInfo() {
    const { data } = await useApi(
      `${calendarEndpoints().getCalendarSpotInfo(authStore.spotId)}`,
    )
      .get()
      .json()

    spotInfo.value = data.value
  }

  function getDefaultFilters() {
    inquiryFilters.value = [...calendarFilters.inquiry]
    marketingFilters.value = [...calendarFilters.marketing]
    waitingOnMeFilter.value = inquiryFiltersStorage.value.waitingOnMeFilter

    if (
      ['marketing_types', 'inquiry_types', 'inquiry_statuses'].some(
        (key) => key in route.query,
      )
    ) {
      inquiryFilters.value.forEach((filter) => {
        if (
          route.query.inquiry_types?.includes(filter.type) ||
          !route.query.inquiry_types
        ) {
          filter.active = true
        } else {
          filter.active = false
        }

        Object.keys(filter.status).forEach((key) => {
          const inquiry_statuses = route.query.inquiry_statuses
            ?.toString()
            .split(',')
            ?.map((item) => item.trim())
          if (
            inquiry_statuses?.includes(key) ||
            !route.query.inquiry_statuses
          ) {
            ;(filter.status as any)[key] = true
          } else {
            ;(filter.status as any)[key] = false
          }
        })
      })

      marketingFilters.value.forEach((filter) => {
        if (
          route.query.marketing_types?.includes(filter.type) ||
          !route.query.marketing_types
        ) {
          filter.active = true
        } else {
          filter.active = false
        }
      })
    } else if (Object.keys(inquiryFiltersStorage.value).length) {
      inquiryFilters.value = JSON.parse(
        JSON.stringify(inquiryFiltersStorage.value.inquiry),
      )
      marketingFilters.value = JSON.parse(
        JSON.stringify(inquiryFiltersStorage.value.marketing),
      )

      waitingOnMeFilter.value = inquiryFiltersStorage.value.waitingOnMeFilter
      return
    }
  }

  async function saveAsDefaultFilters() {
    inquiryFiltersStorage.value = {
      inquiry: inquiryFilters.value as Mutable<INQUIRY_FILTER[]>,
      marketing: marketingFilters.value as Mutable<MARKETING_FILTER[]>,
      waitingOnMeFilter: waitingOnMeFilter.value,
    }
  }

  function getInquiryEndTime(
    inquiry:
      | ENTRY_BY_TYPE['party']
      | ENTRY_BY_TYPE['catering']
      | ENTRY_BY_TYPE['job']
      | ENTRY_BY_TYPE['reservation'],
  ) {
    if (inquiry.type === 'job') {
      return parseISO(inquiry.created_at)
    }

    const currentInqTime = set(new Date(), {
      year: inquiry.year!,
      month: inquiry.month! - 1,
      date: inquiry.date!,
      hours: inquiry.hour!,
      minutes: inquiry.min!,
      seconds: 0,
    })

    return addMinutes(currentInqTime as Date, inquiry.duration_min! ?? 0)
  }

  function isLastCommentPast24hrs(
    inquiry:
      | ENTRY_BY_TYPE['party']
      | ENTRY_BY_TYPE['catering']
      | ENTRY_BY_TYPE['job']
      | ENTRY_BY_TYPE['reservation'],
  ) {
    return (
      inquiry.last_comment &&
      differenceInHours(new Date(), parseISO(inquiry.last_comment.created_at), {
        roundingMethod: 'ceil',
      }) > 24
    )
  }

  const cycleListState = computed(() => {
    return selectedEntry.value?.type === 'website'
      ? websitePromotions.value
      : (entries.value
          .flat()
          .filter((entryOrEntryGroup) => {
            return (
              !('stats' in entryOrEntryGroup && entryOrEntryGroup.stats) &&
              (entryOrEntryGroup as ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE]).type !==
                'blocker' &&
              (entryOrEntryGroup as ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE]).type !==
                'content_nudge'
            )
          })
          .map((entryOrEntryGroup) => {
            if (Array.isArray(entryOrEntryGroup)) {
              return entryOrEntryGroup.map((group) => group.items).flat()
            }

            return entryOrEntryGroup
          })
          .flat() as ENTRY_BY_TYPE[Exclude<keyof ENTRY_BY_TYPE, 'blocker'>][])
  })

  async function approvePost(
    id: Id,
    entryType: Extract<
      ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE]['type'],
      'facebook' | 'instagram' | 'email' | 'text'
    >,
  ) {
    const { data } = await useApi(
      `${calendarEndpoints().calendarApprovePost(authStore.spotId, id)}`,
    )
      .post({
        type: entryType,
      })
      .json()

    for (const entry of entries.value!.flat(2)) {
      if ('items' in entry) continue

      if (entry.id === data.value.id) {
        if (selectedEntry.value && 'status' in selectedEntry.value) {
          selectedEntry.value.status = 'approved'
        }

        Object.assign(entry, data.value)
        // if (entry && 'status' in entry) {
        //   entry.status = 'approved'
        // }
        break
      }
    }
  }

  async function postNow(
    id: Id,
    entryType: Extract<
      ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE]['type'],
      'facebook' | 'instagram' | 'email' | 'text'
    >,
  ) {
    await approvePost(id, entryType)

    await useApi(`${calendarEndpoints().calendarPostNow(authStore.spotId, id)}`)
      .post({
        type: entryType,
      })
      .json()

    for (const entry of entries.value!.flat(2)) {
      if ('items' in entry) continue

      if (entry.id === id) {
        if (selectedEntry.value && 'status' in selectedEntry.value) {
          selectedEntry.value.status = 'sent'
        }

        if (entry && 'status' in entry) {
          entry.status = 'sent'
        }
        break
      }
    }
  }

  async function editPost(
    entry: Exclude<ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE], 'blocker'>,
  ) {
    if (Object.hasOwn(selectedEntryChangedProperties(), 'text')) {
      if (entry.type === 'facebook' || entry.type === 'instagram') {
        entry.changed_description = true
        entry.changed_text = true
      }
    }

    //hack because backend expects text_content
    if (entry.type === 'text') {
      entry.text_content = entry.description
      delete entry.description
    }

    if (
      (selectedCaptionIdea.value && entry.type == 'facebook') ||
      entry.type == 'instagram'
    ) {
      entry.idea = selectedCaptionIdea.value
    }

    const { data } = await useApi(
      `${calendarEndpoints().calendarEditPost(authStore.spotId, entry.id)}`,
    )
      .put(entry)
      .json()

    for (const entry of entries.value!.flat(2)) {
      if ('items' in entry) continue

      if (entry.id === data.value.id) {
        Object.assign(entry, data.value)

        break
      }
    }

    appStore.isLoading = false

    if (
      (selectedCaptionIdea.value && entry.type == 'facebook') ||
      entry.type == 'instagram'
    ) {
      delete entry.idea
    }

    return data.value
  }

  async function deleteEntry(
    selectedEntry: ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE],
  ) {
    appStore.isLoading = true

    await useApi(
      `${calendarEndpoints().calendarDeleteEntry(authStore.spotId, selectedEntry.id)}`,
    )
      .delete({
        type: selectedEntry.type,
      })
      .json()

    //delete from entries
    for (const [entriesArrayIndex, entriesArray] of entries.value.entries()) {
      if (Array.isArray(entriesArray)) {
        for (const [index, entry] of entriesArray.entries()) {
          //delete if entry is an array
          if (Array.isArray(entry)) {
            entry[0].items.forEach(
              (element: ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE]) => {
                if (element.id === selectedEntry.id) {
                  entry[0].items = entry[0].items.filter(
                    (e: ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE]) =>
                      e.id !== selectedEntry.id,
                  )
                }
              },
            )
          }

          if (selectedEntry.id === entry.id) {
            ;(entries.value![
              entriesArrayIndex //typescript is not smart enough to infer this
            ] as unknown as ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE][]) = (
              entriesArray as (typeof selectedEntry)[]
            ).filter(
              (e: ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE]) => e.id !== entry.id,
            )

            break
          }
        }
      }
    }

    await sleep(500)

    appStore.isLoading = false
  }

  async function changeTextCaption(
    entry: ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE],
    idea: CAPTION_IDEA,
  ) {
    const { data } = await useApi(
      calendarEndpoints().calendarChangeTextFragment(
        authStore.spotId,
        entry.id,
      ),
    )
      .put({
        text_fragment_id: idea.id,
        text_fragment_text: idea.text,
        text_fragment_type: idea.type,
        type: entry.type,
      })
      .json()

    return data.value
  }

  function selectedEntryChangedProperties() {
    const realEntry =
      entries.value
        .flat()
        .find(
          (entry: ENTRY_BY_TYPE[keyof ENTRY_BY_TYPE]) =>
            entry.id === selectedEntry.value?.id,
        )! ?? websitePromotions.value

    return findDifferences(toRaw(realEntry), toRaw(selectedEntry.value!))
  }

  //this function is used to set selected entry and avoid triggering "are you sure dialog"
  function setSelectedEntry() {
    for (const entry of entries.value!.flat(2)) {
      if ('items' in entry) continue

      if (entry.id === selectedEntry.value?.id) {
        selectedEntry.value = JSON.parse(JSON.stringify(entry))

        break
      }
    }
  }

  async function updateSpotInfo(payload: DeepPartial<CALENDAR_SPOT_INFO>) {
    const { data } = await useApi(
      calendarEndpoints().updateCalendarSpotInfo(authStore.spotId),
    )
      .put(payload)
      .json()

    // @ts-expect-error: it works fine, not sure why it complains
    spotInfo.value = defu(payload, spotInfo.value)
  }

  async function studioStatusUpdate() {
    const processingEntryIds = entries.value
      .flat()
      .filter(
        (entry) =>
          'studio_processing_status' in entry &&
          entry.studio_processing_status === 'processing',
      )
      .map(({ id }) => id)

    if (!processingEntryIds.length) {
      return pauseStudioStatusUpdatePolling()
    }

    const { data, error } = await useApi(
      calendarEndpoints().calendarMediaEditorQueue(authStore.spotId),
    )
      .post({
        post_ids: processingEntryIds,
      })
      .json<(ENTRY_BY_TYPE['facebook'] | ENTRY_BY_TYPE['instagram'])[]>()

    if (!data.value || error.value) {
      console.error(error.value)
      return pauseStudioStatusUpdatePolling()
    }

    for (const it of data.value) {
      const { dateIndex, entryIndex } = findEntryIndices(it.id)

      if (
        entryIndex !== -1 &&
        (
          entries.value[dateIndex][entryIndex] as ENTRY_BY_TYPE[
            | 'facebook'
            | 'instagram']
        ).studio_processing_status !== it.studio_processing_status
      ) {
        const currentEntry = entries.value[dateIndex][
          entryIndex
        ] as ENTRY_BY_TYPE['facebook' | 'instagram']

        entries.value[dateIndex][entryIndex] = it

        if (
          currentEntry.variant !== 'post' &&
          !thumbnailStore.items.has(it.video_url)
        ) {
          const existingVideoThumbnail = thumbnailStore.items.get(
            currentEntry.video_url,
          )
          thumbnailStore.items.delete(currentEntry.video_url)
          thumbnailStore.items.set(it.video_url, existingVideoThumbnail)
          thumbnailStore.count.increment()
        }

        switch (it.studio_processing_status) {
          case 'edited':
            mediaEditorInfoEditedShownEvent(it.tracking_properties)
            break
          case 'failed':
            mediaEditorInfoFailedShownEvent(it.tracking_properties)
            break
        }

        if (selectedEntry.value?.id === it.id) {
          selectedEntry.value = it
        }
      }
    }
  }

  function findEntryIndices(id: Id) {
    let dateIndex = -1
    let entryIndex = -1

    entries.value.forEach((date, index) => {
      date.forEach((e, eIndex) => {
        if (e.id === id) {
          dateIndex = index
          entryIndex = eIndex
        }
      })
    })

    return {
      dateIndex,
      entryIndex,
    }
  }

  async function studioRetry(
    entry: ENTRY_BY_TYPE['facebook'] | ENTRY_BY_TYPE['instagram'],
  ) {
    const { data, error } = await useApi(
      calendarEndpoints().calendarMediaEditorRetryContentEdit(
        authStore.spotId,
        entry.id,
      ),
    )
      .put()
      .json()

    if (!data.value || error.value) {
      return
    }

    for (const it of data.value) {
      const { dateIndex, entryIndex } = findEntryIndices(it.id)

      if (entryIndex !== -1) {
        entries.value[dateIndex][entryIndex] = it

        if (selectedEntry.value?.id === it.id) {
          selectedEntry.value = it
        }
      }
    }

    if (!isEntryProcessPollingActive.value) {
      resumeStudioStatusUpdatePolling()
    }
  }

  async function studioCancel(
    entry: ENTRY_BY_TYPE['facebook'] | ENTRY_BY_TYPE['instagram'],
  ) {
    const { data, error } = await useApi(
      calendarEndpoints().calendarMediaEditorCancelContentEdit(
        authStore.spotId,
        entry.id,
      ),
    )
      .put()
      .json()

    if (!data.value || error.value) {
      return
    }

    for (const it of data.value) {
      const { dateIndex, entryIndex } = findEntryIndices(it.id)

      if (entryIndex !== -1) {
        entries.value[dateIndex][entryIndex] = it

        if (selectedEntry.value?.id === it.id) {
          selectedEntry.value = it
        }
      }
    }
  }

  return {
    entries,
    currentMonth,
    setCurrentMonth,
    daysInMonth,
    calendarWithEntries,
    websitePromotions,
    inquiryFilters,
    marketingFilters,
    fetchCalendarEntries,
    showEntryDialog,
    selectedEntry,
    selectedGroup,
    showGroupDialog,
    fetchCalendarPromotions,
    getDefaultFilters,
    inquiryFiltersStorage,
    saveAsDefaultFilters,
    waitingOnMeFilter,
    showDatePickerPopUp,
    fetchCalendarBlockedDays,
    blockedDays,
    fetchCalendarSpotInfo,
    spotInfo,
    isLastCommentPast24hrs,
    getInquiryEndTime,
    selectedDate,
    cycleListState,
    approvePost,
    postNow,
    showPostApprovedBtn,
    showSaveChangesDialog,
    editPost,
    showDeleteConfirmationDialog,
    deleteEntry,
    selectedEntryChangedProperties,
    selectedCaptionIdea,
    captionIdeas,
    changeTextCaption,
    setSelectedEntry,
    updateSpotInfo,
    isEntryProcessPollingActive,
    pauseStudioStatusUpdatePolling,
    resumeStudioStatusUpdatePolling,
    studioRetry,
    studioCancel,
    findEntryIndices,
    userChangedText,
  }
})
